/**
 * Deferred asset loaders.
 *
 * These used to sit inline in layout/theme.liquid, which meant the browser re-parsed the
 * same ~2.5 KB of JavaScript on every single page view and could never cache it. Everything
 * that does not depend on Liquid lives here instead; layout/theme.liquid only passes the
 * handful of values it has to compute (asset URLs and two flags) via window.themeLoaders.
 *
 * Loaded with `defer`, so this runs after the document has been parsed but before
 * DOMContentLoaded. That ordering matters: the conditional loaders below query the DOM to
 * decide whether their target markup is present.
 */
(function () {
  var config = window.themeLoaders || {};

  var SHOP_WIDGET_SOURCES = ['https://www.embroidery-app.com/widget.js', 'https://www.sizera.com/widget.js'];

  var HTML2PDF = {
    src: 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js',
    integrity: 'sha512-GsLlZN/3F2ErC5ifS5QtgpiJtWd43JWSuIgh7mbzZ8zBps+dvLusV+eNQATqgA/HdeKFVgA5v3S/cIrLF7QnIg==',
    crossOrigin: 'anonymous',
    referrerPolicy: 'no-referrer'
  };

  function appendScript(options) {
    var script = document.createElement('script');

    if (options.module) {
      script.type = 'module';
    }

    if (options.defer) {
      script.defer = true;
    } else {
      script.async = true;
    }

    if (options.integrity) {
      script.integrity = options.integrity;
    }

    if (options.crossOrigin) {
      script.crossOrigin = options.crossOrigin;
    }

    if (options.referrerPolicy) {
      script.referrerPolicy = options.referrerPolicy;
    }

    script.src = options.src;
    document.head.appendChild(script);
  }

  function once(fn) {
    var called = false;

    return function () {
      if (called) {
        return;
      }

      called = true;
      fn();
    };
  }

  function onFirstInteraction(callback) {
    ['pointerdown', 'keydown', 'touchstart'].forEach(function (type) {
      window.addEventListener(type, callback, { once: true, passive: true });
    });
  }

  function whenIdle(callback, timeout) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: timeout });
    } else {
      window.addEventListener('load', function () {
        setTimeout(callback, timeout / 2);
      });
    }
  }

  /**
   * Scripts that only act on specific markup: the two product-gallery helpers need a
   * rendered <product-gallery>, size-recommendation.js needs a <size-recommendation>.
   * They are requested when that markup is already in the document, and otherwise on
   * first interaction, which is the earliest point a quick-buy or cart drawer can inject it.
   */
  function setUpConditionalScripts() {
    var entries = config.conditional || [];

    if (!entries.length) {
      return;
    }

    var request = entries.map(function (entry) {
      return once(function () {
        appendScript({ src: entry.src, module: entry.module, defer: true });
      });
    });

    entries.forEach(function (entry, index) {
      if (entry.selector && document.querySelector(entry.selector)) {
        request[index]();
      }
    });

    onFirstInteraction(function () {
      request.forEach(function (fn) {
        fn();
      });
    });
  }

  /**
   * The Sizera size advisor and the embroidery personalisation widget are only wired up on
   * product templates and cart line items, where layout/theme.liquid loads them directly.
   * Everywhere else they still have to be available -- a personalised line item can appear
   * in the cart drawer -- but not during page load, so they wait for an interaction with an
   * idle callback as a backstop.
   */
  function setUpShopWidgets() {
    if (!config.deferShopWidgets) {
      return;
    }

    var load = once(function () {
      SHOP_WIDGET_SOURCES.forEach(function (src) {
        appendScript({ src: src });
      });
    });

    onFirstInteraction(load);
    whenIdle(load, 6000);
  }

  /**
   * html2pdf is a ~380 KB bundle that assets/cart.js only touches when a visitor presses the
   * cart PDF button. The button lives in the cart drawer as well as on the cart page, so it
   * cannot be gated by template -- it loads at idle instead of during page load.
   */
  function setUpHtml2Pdf() {
    if (!config.html2pdf) {
      return;
    }

    whenIdle(
      once(function () {
        appendScript(HTML2PDF);
      }),
      8000
    );
  }

  function applyContentOnlyClass() {
    if (window.location.search.indexOf('content-only=true') !== -1) {
      document.body.classList.add('content-only');
    }
  }

  applyContentOnlyClass();
  setUpConditionalScripts();
  setUpShopWidgets();
  setUpHtml2Pdf();
})();
